import type { FeatureFlagKey } from '@sb/feathers-types';
import type { StepKind } from '@sb/routine-runner';

/**
 * The part of a Move Arm step's arguments the permitted-children rule reads.
 * Kept structural so the validation library doesn't depend on the remote-control
 * step definition that owns the full argument type.
 */
export interface MoveArmToV2PermittedChildArgs {
  enforceConstantSpeed?: boolean;
}

/**
 * Shared source of truth for MoveArmToV2 permitted child step kinds.
 * Mirrors the UI behavior but is exported from the validation library
 * so both UI and server can consume the same rule.
 *
 * `args` is the Move step's own configuration. A constant-speed move traces a
 * continuous tooltip path, which is exactly what an arc describes, so it accepts
 * Arc children on its own — independently of the `arcStepInMove` flag, which
 * gates arcs in ordinary point-to-point moves. Callers that have no step to hand
 * (a step-kind-only question, e.g. the step library's static permitted list) may
 * omit it and get the flag-only answer.
 */
export function getPermittedChildStepKindsShared(
  featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {},
  args?: MoveArmToV2PermittedChildArgs,
): readonly StepKind[] {
  const { allowSubstepsMoveStepV2, arcStepInMove } = featureFlags;

  const permittedSteps: StepKind[] = ['Waypoint'];

  if (allowSubstepsMoveStepV2) {
    permittedSteps.push('SetIO', 'SetEnvironmentVariable');
  }

  if (arcStepInMove || args?.enforceConstantSpeed) {
    permittedSteps.push('Arc');
  }

  return permittedSteps;
}
